import React from "react";
import { Box, Pagination, PaginationItem, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import TablePagination from "@mui/material/TablePagination";

interface TablePaginationActionsProps {
  count: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement>,
    newPage: number,
  ) => void;
}

interface CustomPaginationProps {
  totalRowCount: number;
  filters: {
    pageSize: number;
    pageNumber: number;
  };
  onlyAll?: boolean;
  setFilters: any;
}

const CustomTablePagination = styled(TablePagination)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  width: "100%",

  "& .MuiTablePagination-spacer": {
    display: "none",
  },

  "& .MuiTablePagination-toolbar": {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
  },

  "& .MuiTablePagination-selectLabel": {
    marginRight: 0,
  },

  "& .MuiTablePagination-select": {
    marginLeft: 0,
  },

  "& .MuiTablePagination-displayedRows": {
    flexGrow: 1,
    textAlign: "center",
  },

  "& .MuiTablePagination-actions": {
    display: "flex",
    justifyContent: "flex-end",
    flexGrow: 1,
  },
}));

const PaginationLabel = (from: number, to: number, count: number) => {
  return (
    <Box
      sx={{
        display: "inline-block",
        backgroundColor: "#EDF2F7",
        borderRadius: "8px",
        padding: "8px 48px",
        textAlign: "center",
      }}
    >
      <Typography fontWeight={500} color="black" sx={{ fontSize: "14px" }}>
        Page {from} - {to} of {count}
      </Typography>
    </Box>
  );
};

function TablePaginationActions(props: Readonly<TablePaginationActionsProps>) {
  const { count, page, rowsPerPage, onPageChange } = props;

  const handlePageClick = (
    event: React.ChangeEvent<unknown>,
    newPage: number,
  ) => {
    onPageChange(event as any, newPage - 1);
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }} display="flex" alignItems="center">
      <Pagination
        count={Math.ceil(count / rowsPerPage)}
        page={page + 1}
        onChange={handlePageClick}
        renderItem={(item) => (
          <PaginationItem
            slots={{
              previous: KeyboardArrowLeft,
              next: KeyboardArrowRight,
            }}
            {...item}
          />
        )}
      />
    </Box>
  );
}

const CustomPagination: React.FC<CustomPaginationProps> = ({
  totalRowCount,
  filters,
  onlyAll = false,
  setFilters,
}) => {
  const allValue =
    totalRowCount > 0 ? totalRowCount : Math.max(filters.pageSize || 1, 1);
  const rowsPerPageOptions = onlyAll
    ? [{ label: "All", value: allValue }]
    : [1, 5, 10, 25, 50, { label: "All", value: allValue }];

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setFilters((f: any) => ({ ...f, pageNumber: newPage + 1 }));
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setFilters((f: any) => ({
      ...f,
      pageSize: Number.parseInt(event.target.value, 10),
      pageNumber: 1,
    }));
  };
  return (
    <CustomTablePagination
      rowsPerPageOptions={rowsPerPageOptions}
      count={totalRowCount ?? 0}
      rowsPerPage={onlyAll ? allValue : filters.pageSize}
      page={onlyAll ? 0 : filters.pageNumber - 1}
      labelDisplayedRows={({ from, to, count }) =>
        PaginationLabel(from, to, count)
      }
      onPageChange={handleChangePage}
      onRowsPerPageChange={handleChangeRowsPerPage}
      ActionsComponent={TablePaginationActions}
    />
  );
};

export default CustomPagination;
